import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "anchorRef", "offset", "arrowOffset", "direction", "interactive", "onSetDirection", "possibleDirections", "style", "container", "forceRecalculatePosition", "isHideWhenOutOfBlock"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { useCheckOutOfBlock } from "./useCheckOutOfBlock";
import { canUseDOM } from "../../utils/canUseDOM";
import { EXCLUDED_DIRECTIONS } from './constants';
import { defaultPossibleDirections, getComputedPositionAndDirection, getPossibleDirection } from './helpers';
import { useRecalculatePosition } from './useRecalculatePosition';
export var Portal = /*#__PURE__*/forwardRef(function Popover(_ref, ref) {
  var children = _ref.children,
      anchorRef = _ref.anchorRef,
      _ref$offset = _ref.offset,
      offset = _ref$offset === void 0 ? 0 : _ref$offset,
      _ref$arrowOffset = _ref.arrowOffset,
      arrowOffset = _ref$arrowOffset === void 0 ? 0 : _ref$arrowOffset,
      _ref$direction = _ref.direction,
      initDirection = _ref$direction === void 0 ? 'top' : _ref$direction,
      interactive = _ref.interactive,
      onSetDirection = _ref.onSetDirection,
      _ref$possibleDirectio = _ref.possibleDirections,
      possibleDirections = _ref$possibleDirectio === void 0 ? defaultPossibleDirections : _ref$possibleDirectio,
      style = _ref.style,
      _ref$container = _ref.container,
      container = _ref$container === void 0 ? document.body : _ref$container,
      forceRecalculatePosition = _ref.forceRecalculatePosition,
      _ref$isHideWhenOutOfB = _ref.isHideWhenOutOfBlock,
      isHideWhenOutOfBlock = _ref$isHideWhenOutOfB === void 0 ? false : _ref$isHideWhenOutOfB,
      props = _objectWithoutProperties(_ref, _excluded);

  var _useState = useState(),
      _useState2 = _slicedToArray(_useState, 2),
      position = _useState2[0],
      setPosition = _useState2[1];

  var _useState3 = useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      contentNode = _useState4[0],
      setContentNode = _useState4[1];

  var _useState5 = useState(0),
      _useState6 = _slicedToArray(_useState5, 2),
      childrenHeight = _useState6[0],
      setChildrenHeight = _useState6[1];

  var _useState7 = useState(''),
      _useState8 = _slicedToArray(_useState7, 2),
      currentDirection = _useState8[0],
      setCurrentDirection = _useState8[1];

  var protectedPortal = container || document.body;

  var _useCheckOutOfBlock = useCheckOutOfBlock({
    anchorRef: anchorRef,
    position: position,
    container: protectedPortal,
    isHideWhenOutOfBlock: isHideWhenOutOfBlock
  }),
      isOutOfBlock = _useCheckOutOfBlock.isOutOfBlock; // Нужно для того, что бы реагировать на изменения высоты children


  useEffect(function () {
    var _children$ref;

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if ((_children$ref = children.ref) !== null && _children$ref !== void 0 && _children$ref.current) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      setChildrenHeight(children.ref.current.clientHeight);
    }
  }, [children]);
  var contentRef = useCallback(function (content) {
    if (ref) {
      if (typeof ref === 'function') {
        ref(content);
      } else {
        // eslint-disable-next-line no-param-reassign
        ref.current = content;
      }
    }

    if (content !== null) {
      setContentNode(content);
    }
  }, [ref]);
  var updateLocation = useCallback(function () {
    var directionsAndPositions = getComputedPositionAndDirection({
      anchorRef: anchorRef,
      contentNode: contentNode,
      offset: offset,
      arrowOffset: arrowOffset
    });

    var _getPossibleDirection = getPossibleDirection(initDirection, directionsAndPositions, possibleDirections, contentNode),
        direction = _getPossibleDirection.direction,
        newPosition = _getPossibleDirection.position; // храним текущий direction в состояние, для дальнейшего определения позиционирования top/bottom


    setCurrentDirection(direction);

    if (onSetDirection) {
      onSetDirection(direction);
    }

    setPosition(newPosition); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorRef, arrowOffset, contentNode, initDirection, offset, onSetDirection, possibleDirections, forceRecalculatePosition, isOutOfBlock]);
  useEffect(function () {
    if (anchorRef.current && contentNode) {
      updateLocation();
    }
  }, [updateLocation, initDirection, anchorRef, contentNode, childrenHeight]);
  useRecalculatePosition({
    anchorRef: anchorRef,
    onChangePosition: updateLocation
  });
  var isDirectionExcluded = EXCLUDED_DIRECTIONS.includes(currentDirection);
  var positions = useMemo(function () {
    return {
      top: !isDirectionExcluded ? (position === null || position === void 0 ? void 0 : position.y) || 0 : undefined,
      bottom: isDirectionExcluded ? (position === null || position === void 0 ? void 0 : position.y) || 0 : undefined,
      left: ((position === null || position === void 0 ? void 0 : position.x) || 0) + (canUseDOM ? window.pageXOffset : 0)
    };
  }, [isDirectionExcluded, position]);
  return /*#__PURE__*/ReactDOM.createPortal( /*#__PURE__*/React.createElement("div", _extends({}, props, {
    ref: contentRef,
    style: _objectSpread(_objectSpread({}, style), {}, {
      position: 'fixed',
      opacity: position ? 1 : 0,
      top: positions.top,
      bottom: positions.bottom,
      left: positions.left,
      pointerEvents: !interactive ? 'none' : undefined,
      // Хак для избежания проблем с мерцанием
      transition: 'opacity 0.01s ease-in',
      display: isOutOfBlock ? 'none' : 'block',
      zIndex: 998
    })
  }), children), protectedPortal);
});